import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Textarea,
  Card,
  CardBody,
  CardHeader,
  Chip
} from '@heroui/react';
import { PencilIcon, CheckIcon } from '@heroicons/react/24/outline';
import { getAIRewrite } from '../services/aiService';

const RewritePopup = ({ 
  isOpen, 
  onClose, 
  selectedText, 
  onApplyRewrite,
  context = '' 
}) => {
  const [instructions, setInstructions] = useState('');
  const [rewrittenText, setRewrittenText] = useState('');
  const [originalText, setOriginalText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [activePreset, setActivePreset] = useState('');

  // Preset instruction options
  const presetInstructions = [
    { id: 'rewrite', label: 'Rewrite', instruction: 'Rewrite this text to improve clarity and flow while maintaining the original meaning.' },
    { id: 'less-formal', label: 'Less Formal', instruction: 'Make this text less formal and more conversational while keeping it professional.' },
    { id: 'more-formal', label: 'More Formal', instruction: 'Make this text more formal and professional in tone.' },
    { id: 'polish', label: 'Polish', instruction: 'Polish this text to make it more refined, clear, and well-structured.' },
  ];

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setInstructions('');
      setRewrittenText('');
      setOriginalText(selectedText);
      setError('');
      setActivePreset('');
    }
  }, [isOpen, selectedText]);

  const handleRewrite = async (customInstructions = '') => {
    if (!selectedText.trim()) return;

    const instructionsToUse = customInstructions || instructions;
    setIsLoading(true);
    setError('');
    
    try {
      const result = await getAIRewrite(selectedText, context, instructionsToUse);
      
      if (result.error) {
        setError(result.error);
      } else {
        setRewrittenText(result.text);
      }
    } catch (err) {
      console.error('Error rewriting text:', err);
      setError('Failed to rewrite text. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePresetClick = (preset) => {
    setActivePreset(preset.id);
    setInstructions(preset.instruction);
    handleRewrite(preset.instruction);
  };

  const handleApply = () => {
    if (rewrittenText && onApplyRewrite) {
      onApplyRewrite(rewrittenText);
      onClose();
    }
  };

  const handleClose = () => {
    setInstructions('');
    setRewrittenText('');
    setError('');
    setActivePreset('');
    onClose();
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={handleClose}
      size="4xl"
      scrollBehavior="inside"
    >
      <ModalContent>
        <ModalHeader className="flex gap-2 items-center">
          <PencilIcon className="h-5 w-5 text-primary" />
          AI Rewrite Assistant
        </ModalHeader>
        <ModalBody className="gap-4">
          {/* Original Text Display */}
          <Card>
            <CardHeader className="pb-2">
              <h4 className="text-sm font-semibold text-gray-600 dark:text-gray-400">Selected Text</h4>
            </CardHeader>
            <CardBody className="pt-0">
              <p className="text-sm bg-gray-50 dark:bg-gray-800 p-3 rounded-lg border">
                {originalText}
              </p>
            </CardBody>
          </Card>

          {/* Quick Actions */}
          <div>
            <h4 className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-3">Quick Actions</h4>
            <div className="flex flex-wrap gap-2">
              {presetInstructions.map((preset) => (
                <Button
                  key={preset.id}
                  size="sm"
                  variant={activePreset === preset.id ? "solid" : "bordered"}
                  color={activePreset === preset.id ? "primary" : "default"}
                  onPress={() => handlePresetClick(preset)}
                  isLoading={isLoading && activePreset === preset.id}
                  className="min-w-fit"
                >
                  {preset.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Custom Instructions Input */}
          <div>
            <Textarea
              label="Custom Instructions (Optional)"
              placeholder="E.g., Make it more concise, Use active voice, Improve clarity..."
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              variant="bordered"
              minRows={2}
              maxRows={4}
              description="Provide specific instructions for how you'd like the text rewritten, or use the quick actions above."
            />
          </div>

          {/* Custom Rewrite Button */}
          <div className="flex justify-center">
            <Button
              color="secondary"
              onPress={() => handleRewrite()}
              isLoading={isLoading && !activePreset}
              startContent={!isLoading && <PencilIcon className="h-4 w-4" />}
              size="lg"
              isDisabled={!instructions.trim()}
            >
              {isLoading && !activePreset ? 'Rewriting...' : 'Custom Rewrite'}
            </Button>
          </div>

          {/* Error Display */}
          {error && (
            <Card className="border border-red-200 bg-red-50 dark:bg-red-950/20">
              <CardBody>
                <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
              </CardBody>
            </Card>
          )}

          {/* Rewritten Text Display */}
          {rewrittenText && (
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between w-full">
                  <h4 className="text-sm font-semibold text-green-600 dark:text-green-400">AI Rewritten Text</h4>
                  {activePreset && (
                    <Chip size="sm" color="primary" variant="flat">
                      {presetInstructions.find(p => p.id === activePreset)?.label}
                    </Chip>
                  )}
                </div>
              </CardHeader>
              <CardBody className="pt-0">
                <p className="text-sm bg-green-50 dark:bg-green-950/20 p-3 rounded-lg border border-green-200">
                  {rewrittenText}
                </p>
              </CardBody>
            </Card>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="danger" variant="light" onPress={handleClose}>
            Cancel
          </Button>
          {rewrittenText && (
            <Button 
              color="success" 
              onPress={handleApply}
              startContent={<CheckIcon className="h-4 w-4" />}
            >
              Apply Rewrite
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default RewritePopup; 